import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot, LifeBuoy } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiErr } from "@/lib/api";

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", text: "Hi! I'm the PhonePlay Help Assistant. Ask me about Fun Coins, skill duels and tournaments, daily bonuses, or responsible play." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  const QUICK = [
    "How do skill duels work?",
    "How do I earn more Fun Coins?",
    "Can I redeem fun coins for money?",
    "How do tournament prizes work?",
    "How do I set a spending limit?",
  ];

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const sendText = async (raw) => {
    const text = (raw || "").trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/support/chat", { session_id: sessionId, message: text });
      setSessionId(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: apiErr(e) }]);
    } finally { setBusy(false); }
  };
  const send = () => sendText(input);
  const humanHandoff = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/support/handoff", { session_id: sessionId });
      setMsgs((m) => [...m, { role: "assistant", text: data.message }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: apiErr(e) }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="support-chat-toggle" onClick={() => setOpen((o) => !o)}
        className="fixed bottom-3 right-3 sm:bottom-5 sm:right-5 z-[60] w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-amber-500 to-purple-600 shadow-[0_8px_30px_rgba(168,85,247,0.4)] flex items-center justify-center hover:scale-105 transition-transform duration-200">
        {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="support-chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[520px] glass border border-white/15 rounded-2xl flex flex-col overflow-hidden bg-[#0b0d12]/95">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 bg-gradient-to-r from-amber-500/15 to-purple-600/15">
              <Bot className="w-5 h-5 text-amber-400" />
              <div>
                <p className="font-display font-bold text-sm">PhonePlay Help</p>
                <p className="text-[11px] text-gray-400">Support &amp; FAQ assistant</p>
              </div>
              <button data-testid="support-human-handoff" onClick={humanHandoff} disabled={busy}
                className="ml-auto text-[11px] px-2.5 py-1.5 rounded-full bg-white/8 border border-white/15 text-gray-300 hover:text-white hover:border-emerald-500/40 transition-colors duration-200 flex items-center gap-1.5 disabled:opacity-40">
                <LifeBuoy className="w-3.5 h-3.5" /> Talk to a human
              </button>
            </div>

            <div className="flex-1 overflow-y-auto hide-scroll p-3 space-y-3">
              {msgs.map((m, i) => (
                <div key={i} data-testid={`support-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.role === "user" ? "bg-gradient-to-br from-emerald-500 to-cyan-600 text-white rounded-br-sm" : "bg-white/8 text-gray-200 rounded-bl-sm"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="px-3 py-2 rounded-2xl bg-white/8 text-gray-400 text-sm">…</div></div>}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-white/10">
              {!busy && (
                <div className="flex gap-2 overflow-x-auto hide-scroll pb-2 mb-1">
                  {QUICK.map((q) => (
                    <button key={q} data-testid="support-quick-reply" onClick={() => sendText(q)}
                      className="whitespace-nowrap shrink-0 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300 hover:bg-amber-500/15 hover:border-amber-500/40 hover:text-amber-300 transition-colors duration-200">
                      {q}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input data-testid="support-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask a question…"
                  className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-500 transition-colors duration-200" />
                <button data-testid="support-chat-send" onClick={send} disabled={busy || !input.trim()}
                  className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-purple-600 flex items-center justify-center disabled:opacity-40">
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
